import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { node_imageBaseUrl } from "../const";
import { useIndiaStateCity } from "../../utils/useIndiaStateCity";
import { sanitizeMobileInput } from "../../utils/mobileInput";
import { GIRVI_THEME } from "./girviTheme";
import { useButtonTableStyle } from "../../utils/useButtonTableStyle";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import {
  showGirvi,
  updateGirviMultipart,
  createCustomerUser,
} from "../../erpClient";
import "./girvi.css";
import CustomerSelection from "../../components/CustomerSelection";
import {
  createGirviIdempotencyKey,
  isGirviApiSuccess,
} from "../../utils/girviErpAdapter";

// Build safe absolute URLs for images coming from Node API.
// Backend may return:
// - full URL (http...)
// - "upload/..." or "uploads/..."
// - "/upload/..."
// - subpath or plain filename
const getNodeImageCandidates = (rawPath) => {
  if (!rawPath) return [];
  const p = String(rawPath).trim();
  if (!p) return [];

  if (
    p.startsWith("data:") ||
    p.startsWith("blob:") ||
    p.startsWith("http://") ||
    p.startsWith("https://")
  ) {
    return [p];
  }

  const base = String(node_imageBaseUrl || "").replace(/\/+$/, "");
  const normalized = p.replace(/^\/+/, "");

  if (normalized.startsWith("upload/") || normalized.startsWith("uploads/")) {
    return [encodeURI(`${base}/${normalized}`)];
  }

  if (normalized.includes("/")) {
    return [
      encodeURI(`${base}/${normalized}`),
      encodeURI(`${base}/upload/${normalized}`),
    ];
  }

  return [
    encodeURI(`${base}/upload/${normalized}`),
    encodeURI(`${base}/upload/product/${normalized}`),
    encodeURI(`${base}/${normalized}`),
  ];
};

const getNodeImageUrl = (rawPath) => getNodeImageCandidates(rawPath)[0] || null;

const COLUMNS = [
  { id: "no", label: "No.", width: "20px" },
  { id: "productGroup", label: "Product Group", width: "250px" },
  { id: "purity", label: "Purity", width: "80px" },
  { id: "pcs", label: "Pcs", width: "60px" },
  { id: "grWt", label: "Gr.Wt", width: "70px" },
  { id: "lsWt", label: "Ls.Wt", width: "70px" },
  { id: "netWt", label: "Net.Wt", width: "70px" },
  { id: "fWt", label: "Fine.Wt", width: "70px" },
  { id: "itRateOn", label: "RateOn", width: "100px" },
  { id: "itRate", label: "Rate", width: "100px" },
  { id: "itAmt", label: "Amount", width: "100px" },
  { id: "othAmt", label: "Other", width: "120px" },
  { id: "mrp", label: "MRP", width: "100px" },
  { id: "totalAmt", label: "TotalAmt", width: "150px" },
];

const DEFAULT_RATE_ON = "FWt";

const computeNetWeight = (gwt, lwt) => {
  const gross = parseFloat(gwt) || 0;
  const less = parseFloat(lwt) || 0;
  return Math.max(0, gross - less);
};

const computeFineWeight = (gwt, lwt, purity) => {
  const net = computeNetWeight(gwt, lwt);
  const p = parseFloat(purity) || 0;
  if (net <= 0 || p <= 0) return 0;
  return net * (p / 100);
};

const PAYMENT_METHOD_LABELS = {
  cash: "Cash",
  cheque: "Cheque",
  bank_transfer: "Bank Transfer",
  upi: "UPI",
  card: "Card",
  old_gold: "Old Gold",
  old_silver: "Old Silver",
  kasar: "Kasar",
  scheme: "Scheme",
  order: "Order",
};

function EditGirvi({ masterGoldRate, masterSilverRate }) {
  const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();
  const canEditGirvi = hasPermission(PERMISSIONS.EDITGIRVI);
  const girviUpdateIdempotencyKeyRef = useRef(null);
  const girviUpdatePayloadFingerprintRef = useRef(null);
  const location = useLocation();
  const girviId =
    location.state?.girviId || new URLSearchParams(location.search).get("id");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const initialRowData = COLUMNS.map((col) => ({
    id: col.id,
    value: col.id === "itRateOn" ? DEFAULT_RATE_ON : "",
  }));

  const [tableData, setTableData] = useState([{ id: 1, data: initialRowData }]);
  const [itemImage, setItemImage] = useState(null);
  const [itemImagePreview, setItemImagePreview] = useState(null);
  const [existingItemImageName, setExistingItemImageName] = useState(null);
  const [showItemImageModal, setShowItemImageModal] = useState(false);
  const itemImageInputRef = useRef(null);
  const [paymentRows, setPaymentRows] = useState([
    {
      id: 1,
      date: new Date().toISOString().split("T")[0],
      pay_mode: "cash",
      type: "",
      remarks: "",
      amount: "0.00",
    },
    {
      id: 2,
      date: new Date().toISOString().split("T")[0],
      pay_mode: "cheque",
      type: "",
      remarks: "",
      amount: "0.00",
    },
    {
      id: 3,
      date: new Date().toISOString().split("T")[0],
      pay_mode: "bank_transfer",
      type: "",
      remarks: "",
      amount: "0.00",
    },
  ]);
  const [installmentPayments, setInstallmentPayments] = useState([]);
  const [billType, setBillType] = useState("GST");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [customerMobileSearchText, setCustomerMobileSearchText] = useState("");
  const [customerNameSearchText, setCustomerNameSearchText] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [originalCustomerMobile, setOriginalCustomerMobile] = useState("");
  const [customerMobileDropdownActive, setCustomerMobileDropdownActive] =
    useState(false);
  const [customerMobileHighlightedIndex, setCustomerMobileHighlightedIndex] =
    useState(0);
  const customerMobileInputRef = useRef(null);
  const customerMobileDropdownRef = useRef(null);
  const customerMobileItemsRef = useRef([]);
  const [userss, setUserss] = useState([]);
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [customerDetails, setCustomerDetails] = useState({
    email: "",
    birthDate: "",
    anniversaryDate: "",
    companyName: "",
    companyAddress: "",
    gstNumber: "",
    aadharNo: "",
    panNo: "",
    maritalStatus: "",
    balance: "",
    saleBalance: "",
    reference: "",
    state: "",
    area: "",
  });
  const [customerCity, setCustomerCity] = useState("");
  const [customerSaveSuccess, setCustomerSaveSuccess] = useState(false);
  const { states, cities, selectedState, setSelectedState } = useIndiaStateCity();
  const [customerPhotoPreview, setCustomerPhotoPreview] = useState(null);
  const [gstPercent, setGstPercent] = useState("3");
  const [note, setNote] = useState("");
  const inputRefs = useRef({});
  const fileInputRefs = useRef({});
  const [principalGiven, setPrincipalGiven] = useState("");
  const [returnPeriod, setReturnPeriod] = useState("");
  const [returnPeriodType, setReturnPeriodType] = useState("Month");
  const [interestPercent, setInterestPercent] = useState("");
  const [monthlyInterest, setMonthlyInterest] = useState("");
  const [remaining, setRemaining] = useState("0.00");
  const [showProductDetailsModal, setShowProductDetailsModal] = useState(false);
  const [showInstallmentHistoryModal, setShowInstallmentHistoryModal] =
    useState(false);
  const [advancedInterestPayment, setAdvancedInterestPayment] = useState(null);
  const [advancedInterest, setAdvancedInterest] = useState(false);
  const [userProofType, setUserProofType] = useState("");
  const [userProofImg, setUserProofImg] = useState(null);
  const [userProofImgPreview, setUserProofImgPreview] = useState(null);
  const [showUserProofModal, setShowUserProofModal] = useState(false);
  const userProofImgInputRef = useRef(null);
  const [customerSignature, setCustomerSignature] = useState(null);
  const [customerSignaturePreview, setCustomerSignaturePreview] =
    useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const customerSignatureInputRef = useRef(null);

  // Load girvi data for editing
  useEffect(() => {
    const loadGirviData = async () => {
      try {
        setLoading(true);
        const response = await showGirvi({ id: girviId });
        // console.log("All data", response.data);

        if (isGirviApiSuccess(response)) {
          const data = response?.data;
          const firstObj = Array.isArray(data) ? data[0] : data || {};
          const girviDetails =
            firstObj?.girvi_details || firstObj?.detail || firstObj || {};
          const items = firstObj.items || [];
          const payments = firstObj.payments || [];

          // Set basic info
          setDate(
            girviDetails.girvi_date || new Date().toISOString().split("T")[0]
          );
          setSelectedCustomerId(
            girviDetails.customer_id ??
              girviDetails.user_id ??
              girviDetails.party_id ??
              null,
          );
          const loadedMobile =
            girviDetails.customer_mobile ?? girviDetails.mobile ?? "";
          setOriginalCustomerMobile(String(loadedMobile).trim());
          setCustomerMobileSearchText(loadedMobile);
          setCustomerNameSearchText(
            girviDetails.customer_name ?? girviDetails.supplier_name ?? "",
          );

          // Sync with CustomerSelection states
          setCustomerMobile(loadedMobile);
          setCustomerName(
            girviDetails.customer_name ??
              girviDetails.supplier_name ??
              girviDetails.name ??
              "",
          );
          setCustomerAddress(girviDetails.address || "");
          setCustomerCity(girviDetails.city || "");
          setSelectedState(girviDetails.state || ""); // Or match with state ISO if needed

          setCustomerDetails({
            email: girviDetails.email || "",
            birthDate: girviDetails.dob || "", // check if dob or birth_date
            anniversaryDate: girviDetails.doa || "", // check if doa
            companyName: girviDetails.company_name || "",
            companyAddress: girviDetails.company_address || "",
            gstNumber: girviDetails.gst_in || "",
            aadharNo: girviDetails.adhar_card || "",
            panNo: girviDetails.pan_card || "",
            maritalStatus: girviDetails.marital_status || "",
            balance: girviDetails.opening_balance || "",
            saleBalance: "",
            reference: girviDetails.reference || "",
            state: girviDetails.state || "",
            area: girviDetails.area || "",
          });

          setNote(girviDetails.remarks || "");
          setPrincipalGiven(String(girviDetails.principal_given || ""));
          // API may send `return_period` or `tenure_months` (flat or nested).
          const resolvedReturnPeriod =
            girviDetails.return_period ??
            girviDetails.tenure_months ??
            firstObj.return_period ??
            firstObj.tenure_months;
          setReturnPeriod(
            resolvedReturnPeriod != null && resolvedReturnPeriod !== ""
              ? String(resolvedReturnPeriod)
              : "",
          );
          setReturnPeriodType(
            girviDetails.return_period_type ??
              firstObj.return_period_type ??
              "Month",
          );
          setInterestPercent(String(girviDetails.interest_percent || ""));
          setMonthlyInterest(String(girviDetails.monthly_interest || ""));
          setAdvancedInterest(
            Boolean(
              girviDetails.advanced_interest === 1 ||
                girviDetails.monthly_interest_checked === 1 ||
                (Array.isArray(payments) &&
                  payments.some(
                    (p) =>
                      (String(p.remarks || "")
                        .toLowerCase()
                        .includes("advanced interest") &&
                        String(p.pay_type || "").toUpperCase() === "AAVAK") ||
                      String(p.remarks || "")
                        .toLowerCase()
                        .includes("advance interest")
                  ))
            )
          );
          setUserProofType(girviDetails.user_proof_type || firstObj.user_proof_type || "");
          const proofImgPath =
            girviDetails.user_proof_img || firstObj.user_proof_img || "";
          if (proofImgPath) {
            setUserProofImgPreview(getNodeImageUrl(proofImgPath));
          }
          const signaturePath =
            girviDetails.customer_signature ||
            firstObj.customer_signature ||
            "";
          if (signaturePath) {
            setCustomerSignaturePreview(getNodeImageUrl(signaturePath));
          }

          // Form-level item image: document field items_image, or first item.image
          const itemImgPath =
            firstObj.items_image ||
            girviDetails.items_image ||
            items.find((it) => it.image)?.image ||
            null;
          if (itemImgPath) {
            setExistingItemImageName(itemImgPath);
            setItemImagePreview(getNodeImageUrl(itemImgPath));
          }

          // Load items into table
          if (items.length > 0) {
            const mappedItems = items.map((item, index) => {
              const gwt = item.gwt || 0;
              const lwt = item.lwt || 0;
              const purity = item.purity ?? item.touch ?? "";
              const nwtVal =
                item.nwt != null && item.nwt !== ""
                  ? item.nwt
                  : computeNetWeight(gwt, lwt);
              const fwtVal =
                item.fine_wt != null && item.fine_wt !== ""
                  ? item.fine_wt
                  : computeFineWeight(gwt, lwt, purity);

              return {
                id: index + 1,
                girviItemId: item.girvi_item_id ?? item.id,
                existingImage: item.image || itemImgPath || null,
                data: COLUMNS.map((col) => {
                  const valueMap = {
                    tagNo: item.tag_no || "",
                    productGroup: String(
                      item.product_group_id ?? item.product_group ?? "",
                    ),
                    purity: String(purity || ""),
                    pcs: String(item.pcs || ""),
                    grWt: String(gwt || ""),
                    lsWt: String(lwt || ""),
                    netWt: nwtVal !== "" && nwtVal != null ? String(nwtVal) : "",
                    fWt:
                      fwtVal !== "" && fwtVal != null
                        ? String(Number(fwtVal).toFixed(3))
                        : "",
                    itRateOn: item.rate_on || DEFAULT_RATE_ON,
                    itRate: String(item.rate || ""),
                    itAmt: String(
                      item.totalamt ??
                        (item.rate * (item.nwt || 0) || ""),
                    ),
                    lbrPercentage: "",
                    lbrRate: "",
                    lbrAmt: "",
                    othAmt: String(item.other_charges || ""),
                    mrp: String(item.mrp || ""),
                    totalAmt: String(item.totalamt || ""),
                  };
                  return {
                    id: col.id,
                    value: valueMap[col.id] || "",
                    ...(col.id === "productGroup"
                      ? { displayValue: item.product_group_name || "" }
                      : {}),
                  };
                }),
              };
            });
            setTableData(mappedItems);
          }

          // Load payments
          const girviDate =
            girviDetails.girvi_date || new Date().toISOString().split("T")[0];

          const initialPayments = [];
          const installments = [];

          if (Array.isArray(payments)) {
            payments.forEach((p) => {
              // Assume payments on the same date as girvi creation are initial payments
              // Also check if remarks contains "Advanced Interest" - if so, it might be handled separately or just shown
              const pDate = p.payment_date
                ? p.payment_date.split("T")[0]
                : p.date
                ? String(p.date).split("T")[0]
                : "";
              if (pDate === girviDate) {
                initialPayments.push(p);
              } else {
                installments.push(p);
              }
            });
            const aiCandidates = payments.filter((p) =>
              String(p.remarks || "")
                .toLowerCase()
                .includes("advanced interest")
            );
            const aiOnGirviDate =
              aiCandidates.find(
                (p) =>
                  (p.payment_date
                    ? p.payment_date.split("T")[0]
                    : p.date
                    ? String(p.date).split("T")[0]
                    : "") === girviDate
              ) || null;
            setAdvancedInterestPayment(
              aiOnGirviDate ||
                (aiCandidates.length > 0 ? aiCandidates[0] : null)
            );
          }

          // Map initial payments to rows
          const mappedInitialRows = initialPayments.map((p, idx) => {
            // Reverse map label to key
            const modeLabel = p.pay_mode;
            const modeKey =
              Object.keys(PAYMENT_METHOD_LABELS).find(
                (key) => PAYMENT_METHOD_LABELS[key] === modeLabel
              ) ||
              modeLabel?.toLowerCase() ||
              "cash";

            return {
              id: idx + 1,
              payment_id: p.id,
              date: p.payment_date
                ? p.payment_date.split("T")[0]
                : p.date
                ? String(p.date).split("T")[0]
                : girviDate,
              pay_mode: modeKey,
              type: String(p.bank_type || p.bank_id || ""),
              remarks: p.remarks || "",
              amount: String(p.amount || p.amt || "0.00"),
            };
          });

          // Fill up to 3 rows if needed
          const defaultModes = ["cash", "cheque", "bank_transfer"];
          let finalPaymentRows = [...mappedInitialRows];
          if (finalPaymentRows.length < 3) {
            for (let i = finalPaymentRows.length; i < 3; i++) {
              finalPaymentRows.push({
                id: i + 1,
                date: girviDate,
                pay_mode: defaultModes[i] || "cash",
                type: "",
                remarks: "",
                amount: "0.00",
              });
            }
          }
          setPaymentRows(finalPaymentRows);
          setInstallmentPayments(installments);
        } else {
          toast.error("Failed to load Girvi data");
          navigate("/girvitable");
        }
      } catch (error) {
        console.error("Error loading Girvi data:", error);
        toast.error("Failed to load Girvi data");
        navigate("/girvitable");
      } finally {
        setLoading(false);
      }
    };

    if (girviId) {
      loadGirviData();
    } else {
      toast.error("Girvi ID is required");
      navigate("/girvitable");
    }
  }, [girviId]);

  // Listen for API URL changes and reload girvi data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in EditGirvi, reloading page...', event.detail);
      // For edit page, safest to navigate back to table as the edited girvi may not exist in different DB
      navigate("/girvitable");
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);
    
    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, [navigate]);

  const handleInputChange = (rowIndex, colIndex, value) => {
    setTableData((prevData) => {
      const updatedData = [...prevData];
      updatedData[rowIndex].data[colIndex].value = value;
      return updatedData;
    });
  };

  const handleDeleteRow = (rowIndex) => {
    if (tableData.length > 1) {
      setTableData((prevData) =>
        prevData.filter((_, index) => index !== rowIndex)
      );
    }
  };

  const handleAddRow = () => {
    const newRow = {
      id: tableData.length + 1,
      girviItemId: null,
      data: initialRowData,
    };
    setTableData((prevData) => [...prevData, newRow]);
  };

  const handlePaymentRowChange = (rowId, field, value) => {
    setPaymentRows((prevRows) =>
      prevRows.map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row
      )
    );
  };

  const calculateTotal = () => {
    return tableData.reduce((sum, row) => {
      const totalAmt = parseFloat(
        row.data.find((cell) => cell.id === "totalAmt")?.value || 0
      );
      return sum + (isNaN(totalAmt) ? 0 : totalAmt);
    }, 0);
  };

  const AVAILABLE_PAYMENT_METHODS = [
    "cash",
    "cheque",
    "bank_transfer",
    "upi",
    "card",
    "old_gold",
    "old_silver",
    "kasar",
    "scheme",
    "order",
  ];

  const PAYMENT_COLUMNS = [
    { id: "no", label: "No.", width: "30px" },
    { id: "pay_mode", label: "Mode", width: "140px" },
    { id: "type", label: "Type", width: "140px" },
    { id: "remarks", label: "Remarks", width: "180px" },
    { id: "amount", label: "Amt", width: "110px" },
  ];

  const calculateInterestAmount = () => {
    if (monthlyInterest && parseFloat(monthlyInterest) > 0) {
      return parseFloat(monthlyInterest);
    }
    const principal = parseFloat(principalGiven) || 0;
    const interest = parseFloat(interestPercent) || 0;
    return (principal * interest) / 100;
  };

  useEffect(() => {
    const principal = parseFloat(principalGiven || 0);
    setRemaining(principal.toFixed(2));
  }, [principalGiven]);

  const handleSave = async () => {
    if (!canEditGirvi) {
      toast.error("Not authorized to edit Dihran");
      return;
    }
    if (!girviId) {
      toast.error("Girvi id is missing");
      return;
    }
    if (!date) {
      toast.error("Please select a date");
      return;
    }

    setSaving(true);

    try {
      // Build items payload
      const validItems = tableData
        .map((row, idx) => {
          const getValue = (id) => {
            const cell = row.data.find((c) => c.id === id);
            return cell?.value || "";
          };

          const gwtVal = parseFloat(getValue("grWt") || 0) || 0;
          const lwtVal = parseFloat(getValue("lsWt") || 0) || 0;
          const purityValue = parseFloat(getValue("purity") || 0) || 0;
          const fWtVal = parseFloat(getValue("fWt") || 0) || 0;
          const fineWt =
            fWtVal > 0
              ? fWtVal
              : Number(computeFineWeight(gwtVal, lwtVal, purityValue).toFixed(4));

          // Form-level item image applies to first line; keep existing path otherwise
          let imageName = row.existingImage || null;
          if (idx === 0) {
            imageName =
              (itemImage && itemImage.name) ||
              existingItemImageName ||
              row.existingImage ||
              null;
          }

          const totalAmt = parseFloat(getValue("totalAmt") || 0) || 0;
          return {
            row,
            totalAmt,
            payload: {
              product_group_id: parseInt(getValue("productGroup") || 0) || 0,
              pcs: parseFloat(getValue("pcs") || 0) || 0,
              gwt: gwtVal,
              lwt: lwtVal,
              nwt: parseFloat(getValue("netWt") || 0) || 0,
              purity: purityValue,
              fine_wt: fineWt,
              rate_on: getValue("itRateOn") || DEFAULT_RATE_ON,
              rate: parseFloat(getValue("itRate") || 0) || 0,
              other_charges: parseFloat(getValue("othAmt") || 0) || 0,
              totalamt: totalAmt,
              mrp: parseFloat(getValue("mrp") || 0) || 0,
              remarks: "",
              status: 1,
              image: imageName,
            },
          };
        })
        .filter((x) => x.totalAmt > 0);

      if (validItems.length === 0) {
        toast.error("Please add at least one item");
        return;
      }

      const itemsForApi = validItems.map((x) => x.payload);

      const numericGirviId = parseInt(girviId, 10);
      const principalGivenVal = parseFloat(principalGiven) || 0;
      const interestPercentVal = parseFloat(interestPercent) || 0;

      const basePayloadForIdempotency = {
        type: "update",
        girvi_id: numericGirviId,
        girvi_date: date,
        principal_given: principalGivenVal,
        interest_percent: interestPercentVal,
        return_period: parseInt(returnPeriod) || 0,
        return_period_type: returnPeriodType || "Month",
        items: itemsForApi.map((it) => ({
          product_group_id: it.product_group_id,
          pcs: it.pcs,
          gwt: it.gwt,
          lwt: it.lwt,
          nwt: it.nwt,
          purity: it.purity,
          fine_wt: it.fine_wt,
          rate_on: it.rate_on,
          rate: it.rate,
          other_charges: it.other_charges,
          totalamt: it.totalamt,
          mrp: it.mrp,
          remarks: it.remarks,
          status: it.status,
          image: it.image,
        })),
      };

      const fingerprint = JSON.stringify(basePayloadForIdempotency);
      if (
        !girviUpdateIdempotencyKeyRef.current ||
        girviUpdatePayloadFingerprintRef.current !== fingerprint
      ) {
        girviUpdateIdempotencyKeyRef.current = createGirviIdempotencyKey();
        girviUpdatePayloadFingerprintRef.current = fingerprint;
      }

      const formDataForApi = new FormData();
      formDataForApi.append("type", "update");
      formDataForApi.append(
        "idempotency_key",
        girviUpdateIdempotencyKeyRef.current,
      );
      formDataForApi.append("girvi_id", String(numericGirviId));
      formDataForApi.append(
        "principal_given",
        String(principalGivenVal),
      );
      formDataForApi.append(
        "interest_percent",
        String(interestPercentVal),
      );
      formDataForApi.append("girvi_date", date);
      formDataForApi.append(
        "return_period",
        String(parseInt(returnPeriod) || 0),
      );
      formDataForApi.append(
        "return_period_type",
        returnPeriodType || "Month",
      );
      formDataForApi.append("items", JSON.stringify(itemsForApi));

      if (userProofType) {
        formDataForApi.append("user_proof_type", userProofType);
      }
      if (userProofImg) {
        formDataForApi.append(
          "user_proof_img",
          userProofImg,
          userProofImg.name || "user_proof_img",
        );
      }
      if (customerSignature) {
        formDataForApi.append(
          "customer_signature",
          customerSignature,
          customerSignature.name || "customer_signature",
        );
      }

      // New form-level item image only (first item line)
      if (itemImage) {
        formDataForApi.append(
          "image_0",
          itemImage,
          itemImage.name || "item_image",
        );
      }

      const response = await updateGirviMultipart(formDataForApi);
      const updatedGirviId =
        response?.data?.girvi_document_id ??
        response?.girvi_document_id ??
        numericGirviId;

      if (isGirviApiSuccess(response) && updatedGirviId) {
        toast.success("Girvi updated successfully!");
        try {
          await showGirvi({ id: updatedGirviId });
        } catch {
          // ignore refresh errors
        }
        girviUpdateIdempotencyKeyRef.current = null;
        girviUpdatePayloadFingerprintRef.current = null;
        navigate("/girvitable");
      } else {
        toast.error(response?.message || "Failed to update Girvi");
      }
    } catch (error) {
      console.error("Error updating Girvi:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update Girvi",
      );
    } finally {
      setSaving(false);
    }
  };

  // Customer dropdown handlers (similar to Girvi.jsx)
  const handleCustomerMobileChange = (e) => {
    const value = sanitizeMobileInput(e.target.value);
    setCustomerMobileSearchText(value);
    setCustomerMobile(value);
    setCustomerMobileDropdownActive(true);
    setCustomerMobileHighlightedIndex(0);
  };

  const getFilteredUserssByMobile = (searchText) => {
    if (!searchText) return userss;
    const searchLower = searchText.toLowerCase();
    return userss.filter((item) =>
      item.mobile.toLowerCase().includes(searchLower)
    );
  };

  const handleCustomerMobileSelect = (selectedItem) => {
    setCustomerMobileSearchText(selectedItem.mobile);
    setCustomerMobileDropdownActive(false);
    setSelectedCustomerId(selectedItem.id);
    setCustomerNameSearchText(selectedItem.fullName);
  };

  const handleCustomerMobileKeyDown = (e) => {
    const filtered = getFilteredUserssByMobile(customerMobileSearchText);
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setCustomerMobileHighlightedIndex((prevIndex) =>
          prevIndex < filtered.length - 1 ? prevIndex + 1 : prevIndex
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setCustomerMobileHighlightedIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : 0
        );
        break;
      case "Enter":
        e.preventDefault();
        if (filtered.length > 0 && customerMobileDropdownActive) {
          handleCustomerMobileSelect(filtered[customerMobileHighlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setCustomerMobileDropdownActive(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        customerMobileDropdownRef.current &&
        !customerMobileDropdownRef.current.contains(event.target) &&
        customerMobileInputRef.current &&
        !customerMobileInputRef.current.contains(event.target)
      ) {
        setCustomerMobileDropdownActive(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderCustomerMobileDropdown = () => {
    if (!customerMobileDropdownActive) return null;
    const filtered = getFilteredUserssByMobile(customerMobileSearchText);
    customerMobileItemsRef.current = [];
    const inputWidth = customerMobileInputRef.current?.offsetWidth || 150;
    const inputRect = customerMobileInputRef.current?.getBoundingClientRect();

    if (!inputRect) return null;

    return ReactDOM.createPortal(
      <div
        ref={customerMobileDropdownRef}
        className="dropdown-menu show shadow-sm girvi-customer-dropdown"
        style={{
          position: "fixed",
          top: `${inputRect.bottom}px`,
          left: `${inputRect.left}px`,
          width: `${inputWidth}px`,
          maxHeight: "150px",
          overflowY: "auto",
          zIndex: 9999,
          border: `1px solid ${GIRVI_THEME.borderColor}`,
          borderRadius: "0.5rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
        }}
      >
        {filtered.length > 0 ? (
          filtered.map((item, idx) => (
            <div
              key={idx}
              ref={(el) => (customerMobileItemsRef.current[idx] = el)}
              className="dropdown-item"
              style={{
                cursor: "pointer",
                padding: "0.25rem 1rem",
                fontSize: "0.85rem",
                backgroundColor:
                  idx === customerMobileHighlightedIndex
                    ? GIRVI_THEME.bgColor
                    : "transparent",
                color:
                  idx === customerMobileHighlightedIndex
                    ? GIRVI_THEME.textColor
                    : "inherit",
              }}
              onMouseEnter={() => setCustomerMobileHighlightedIndex(idx)}
              onMouseDown={() => handleCustomerMobileSelect(item)}
            >
              {item.mobile} - {item.name}
            </div>
          ))
        ) : (
          <div className="dropdown-item disabled">No matching users</div>
        )}
      </div>,
      document.getElementById("dropdown-portal") || document.body
    );
  };

  if (loading) {
    return (
      <div
        className="body-wrapper d-flex align-items-center justify-content-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="girvi-spinner"></div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          .girvi-customer-dropdown .dropdown-item:hover,
          .girvi-customer-dropdown .dropdown-item.active {
            background-color: ${GIRVI_THEME.bgColor} !important;
            color: ${GIRVI_THEME.textColor} !important;
          }
        `}
      </style>
      <div className="body-wrapper">
        <div className="container-fluid">
          <div
            className={`card w-100 position-relative overflow-hidden ${cardClass} ${
              buttonTableStyle === "square" ? "rounded-0" : ""
            }`}
          >
            <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Edit Dihran</h4>
              <div className="d-flex align-items-center">
                <label className="mb-0 me-2 small fw-semibold text-muted">
                  Date:
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={{
                    width: "160px",
                    borderRadius: "8px",
                    borderColor: GIRVI_THEME.borderColor,
                  }}
                />
              </div>
            </div>
            <div className="card-body p-4">
              <CustomerSelection
                customerMobile={customerMobile}
                setCustomerMobile={setCustomerMobile}
                customerMobileSearchText={customerMobileSearchText}
                setCustomerMobileSearchText={setCustomerMobileSearchText}
                customerName={customerName}
                setCustomerName={setCustomerName}
                customerNameSearchText={customerNameSearchText}
                setCustomerNameSearchText={setCustomerNameSearchText}
                customerAddress={customerAddress}
                setCustomerAddress={setCustomerAddress}
                customerDetails={customerDetails}
                setCustomerDetails={setCustomerDetails}
                customerCity={customerCity}
                setCustomerCity={setCustomerCity}
                selectedCustomerId={selectedCustomerId}
                setSelectedCustomerId={setSelectedCustomerId}
                customerSaveSuccess={customerSaveSuccess}
                setCustomerSaveSuccess={setCustomerSaveSuccess}
                users={userss}
                setUsers={setUserss}
                states={states}
                cities={cities}
                selectedState={selectedState}
                setSelectedState={setSelectedState}
                buttonClass={buttonClass}
                hideAddButton={true}
                excludeMobile={originalCustomerMobile}
              />
              <hr
                className="my-4"
                style={{ borderTop: "1px solid #dcdcdc", opacity: 1 }}
              />
              <div className="row">
                <div className="col-12 col-xl-9">
                  <h6
                    className="fw-bold mb-3 d-flex align-items-center"
                    style={{ color: GIRVI_THEME.bgColor }}
                  >
                    Dihran Details
                  </h6>
                  <div className="col-12 mb-2">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold text-muted mb-1">
                          Item Value (Total)
                        </label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          readOnly
                          value={calculateTotal().toFixed(2)}
                          style={{
                            borderRadius: "8px",
                            fontWeight: "600",
                            borderColor: GIRVI_THEME.borderColor,
                          }}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold text-muted mb-1">
                          Principal Given
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={principalGiven}
                          onChange={(e) => {
                            setPrincipalGiven(e.target.value);
                            if (interestPercent) {
                              const principal = parseFloat(e.target.value) || 0;
                              const interest = parseFloat(interestPercent) || 0;
                              setMonthlyInterest(
                                ((principal * interest) / 100).toFixed(2)
                              );
                            }
                          }}
                          placeholder="Enter amount"
                          step="0.01"
                          style={{
                            borderRadius: "8px",
                            borderColor: GIRVI_THEME.borderColor,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-12 mb-2">
                    <div className="row g-3 align-items-end">
                      <div className="col-md-4">
                        <label className="small fw-semibold text-muted mb-2 d-block">
                          Item Image
                        </label>
                        <div className="d-flex gap-2 align-items-center">
                          <input
                            ref={itemImageInputRef}
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setItemImage(file);
                                setExistingItemImageName(file.name);
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setItemImagePreview(reader.result);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            style={{
                              borderRadius: "8px",
                              borderColor: GIRVI_THEME.borderColor,
                              padding: "0.375rem 0.75rem",
                              height: "38px",
                              lineHeight: "1.5",
                              fontSize: "0.9rem",
                              width: "100%",
                            }}
                          />
                          {itemImagePreview && (
                            <button
                              type="button"
                              className="btn d-flex align-items-center justify-content-center flex-shrink-0"
                              onClick={() => setShowItemImageModal(true)}
                              style={{
                                backgroundColor: GIRVI_THEME.bgColor,
                                color: GIRVI_THEME.textColor,
                                borderRadius: "8px",
                                minWidth: "45px",
                                width: "45px",
                                height: "38px",
                                padding: "0",
                                border: "none",
                              }}
                            >
                              <iconify-icon
                                icon="solar:eye-bold-duotone"
                                width="18"
                                height="18"
                                style={{ color: "#ffffff" }}
                              />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <label className="small fw-semibold text-muted mb-2 d-block">
                          Return Period
                        </label>
                        <div className="d-flex gap-2">
                          <select
                            className="form-select"
                            value={returnPeriodType}
                            onChange={(e) =>
                              setReturnPeriodType(e.target.value)
                            }
                            style={{
                              borderRadius: "8px",
                              borderColor: GIRVI_THEME.borderColor,
                              width: "40%",
                              minWidth: "120px",
                            }}
                          >
                            <option value="Day">Day</option>
                            <option value="Month">Month</option>
                          </select>
                          <input
                            type="number"
                            className="form-control"
                            placeholder={
                              returnPeriodType === "Day"
                                ? "Enter days"
                                : "Enter months"
                            }
                            value={returnPeriod}
                            onChange={(e) => setReturnPeriod(e.target.value)}
                            min="1"
                            style={{
                              borderRadius: "8px",
                              borderColor: GIRVI_THEME.borderColor,
                              flex: 1,
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <label className="small fw-semibold text-muted mb-2 d-block">
                          Interest(%)
                        </label>
                        <div className="d-flex gap-3 align-items-center">
                          <input
                            id="interestPercentInput"
                            type="number"
                            className="form-control"
                            placeholder="Enter rate"
                            value={interestPercent}
                            onChange={(e) => {
                              setInterestPercent(e.target.value);
                              if (principalGiven) {
                                const principal =
                                  parseFloat(principalGiven) || 0;
                                const interest =
                                  parseFloat(e.target.value) || 0;
                                setMonthlyInterest(
                                  ((principal * interest) / 100).toFixed(2)
                                );
                              }
                            }}
                            min="0"
                            step="0.01"
                            style={{
                              borderRadius: "8px",
                              borderColor: GIRVI_THEME.borderColor,
                              flex: 1,
                              minWidth: "120px",
                            }}
                          />
                          <div className="form-check mb-0 flex-shrink-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="advancedInterestCheck"
                              checked={advancedInterest}
                              onChange={(e) =>
                                setAdvancedInterest(e.target.checked)
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="advancedInterestCheck"
                            >
                              Advanced Interest
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 mb-2">
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label small fw-semibold text-muted mb-1">
                          User Proof Type
                        </label>
                        <select
                          className="form-select"
                          value={userProofType}
                          onChange={(e) => setUserProofType(e.target.value)}
                          style={{
                            borderRadius: "8px",
                            borderColor: GIRVI_THEME.borderColor,
                          }}
                        >
                          <option value="">Select Proof Type</option>
                          <option value="Aadhar">Aadhar Card</option>
                          <option value="PAN">PAN Card</option>
                          <option value="Driving License">
                            Driving License
                          </option>
                          <option value="Voter ID">Voter ID</option>
                          <option value="Passport">Passport</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-semibold text-muted mb-1">
                          User Proof Image
                        </label>
                        <div className="d-flex gap-2 align-items-center">
                          <input
                            ref={userProofImgInputRef}
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setUserProofImg(file);
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setUserProofImgPreview(reader.result);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            style={{
                              borderRadius: "8px",
                              borderColor: GIRVI_THEME.borderColor,
                              padding: "0.375rem 0.75rem",
                              height: "38px",
                              lineHeight: "1.5",
                              fontSize: "0.9rem",
                            }}
                          />
                          {userProofImgPreview && (
                            <button
                              type="button"
                              className="btn d-flex align-items-center justify-content-center"
                              onClick={() => setShowUserProofModal(true)}
                              style={{
                                backgroundColor: GIRVI_THEME.bgColor,
                                color: GIRVI_THEME.textColor,
                                borderRadius: "8px",
                                minWidth: "45px",
                                width: "45px",
                                height: "38px",
                                padding: "0",
                                border: "none",
                              }}
                            >
                              <iconify-icon
                                icon="solar:eye-bold-duotone"
                                width="18"
                                height="18"
                                style={{ color: "#ffffff" }}
                              />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-semibold text-muted mb-1">
                          Customer Signature
                        </label>
                        <div className="d-flex gap-2 align-items-center">
                          <input
                            ref={customerSignatureInputRef}
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setCustomerSignature(file);
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setCustomerSignaturePreview(reader.result);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            style={{
                              borderRadius: "8px",
                              borderColor: GIRVI_THEME.borderColor,
                              padding: "0.375rem 0.75rem",
                              height: "38px",
                              lineHeight: "1.5",
                              fontSize: "0.9rem",
                            }}
                          />
                          {customerSignaturePreview && (
                            <button
                              type="button"
                              className="btn d-flex align-items-center justify-content-center"
                              onClick={() => setShowSignatureModal(true)}
                              style={{
                                backgroundColor: GIRVI_THEME.bgColor,
                                color: GIRVI_THEME.textColor,
                                borderRadius: "8px",
                                minWidth: "45px",
                                width: "45px",
                                height: "38px",
                                padding: "0",
                                border: "none",
                              }}
                            >
                              <iconify-icon
                                icon="solar:eye-bold-duotone"
                                width="18"
                                height="18"
                                style={{ color: "#ffffff" }}
                              />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-semibold text-muted mb-1">
                      Remarks
                    </label>
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder="Enter remarks..."
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      maxLength={200}
                      style={{
                        borderRadius: "8px",
                        borderColor: GIRVI_THEME.borderColor,
                        fontSize: "0.9rem",
                      }}
                    />
                  </div>
                  <hr className="my-3" />
                  {installmentPayments.length > 0 && (
                    <div
                      className="card border-0 shadow-sm mb-3"
                      style={{ borderRadius: "12px" }}
                    >
                      <div className="card-body p-3">
                        <h6
                          className="fw-bold mb-3 d-flex align-items-center"
                          style={{ color: GIRVI_THEME.bgColor }}
                        >
                          Installment History
                        </h6>
                        <div className="table-responsive">
                          <table
                            className={`table table-bordered m-0 ${tableClass}`}
                          >
                            <thead>
                              <tr
                                style={{ backgroundColor: GIRVI_THEME.bgLight }}
                              >
                                <th
                                  className="p-2 text-center"
                                  style={{
                                    width: "30px",
                                    color: GIRVI_THEME.bgColor,
                                    fontSize: "0.85rem",
                                    fontWeight: "600",
                                  }}
                                >
                                  No.
                                </th>
                                <th
                                  className="p-2 text-center"
                                  style={{
                                    width: "120px",
                                    color: GIRVI_THEME.bgColor,
                                    fontSize: "0.85rem",
                                    fontWeight: "600",
                                  }}
                                >
                                  Date
                                </th>
                                <th
                                  className="p-2 text-center"
                                  style={{
                                    width: "140px",
                                    color: GIRVI_THEME.bgColor,
                                    fontSize: "0.85rem",
                                    fontWeight: "600",
                                  }}
                                >
                                  Mode
                                </th>
                                <th
                                  className="p-2 text-center"
                                  style={{
                                    width: "140px",
                                    color: GIRVI_THEME.bgColor,
                                    fontSize: "0.85rem",
                                    fontWeight: "600",
                                  }}
                                >
                                  Type
                                </th>
                                <th
                                  className="p-2 text-center"
                                  style={{
                                    width: "180px",
                                    color: GIRVI_THEME.bgColor,
                                    fontSize: "0.85rem",
                                    fontWeight: "600",
                                  }}
                                >
                                  Remarks
                                </th>
                                <th
                                  className="p-2 text-center"
                                  style={{
                                    width: "110px",
                                    color: GIRVI_THEME.bgColor,
                                    fontSize: "0.85rem",
                                    fontWeight: "600",
                                  }}
                                >
                                  Amt
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {installmentPayments.map((row, index) => (
                                <tr
                                  key={index}
                                  style={{
                                    backgroundColor:
                                      index % 2 === 0 ? "#ffffff" : "#fafafa",
                                  }}
                                >
                                  <td
                                    className="p-2 text-center align-middle"
                                    style={{ fontSize: "0.85rem" }}
                                  >
                                    {index + 1}
                                  </td>
                                  <td
                                    className="p-2 text-center align-middle"
                                    style={{ fontSize: "0.85rem" }}
                                  >
                                    {(() => {
                                      const d = new Date(
                                        row.created_at ||
                                          row.payment_date ||
                                          row.date ||
                                          ""
                                      );
                                      return isNaN(d.getTime())
                                        ? ""
                                        : d.toLocaleDateString("en-GB");
                                    })()}
                                  </td>
                                  <td
                                    className="p-2 text-center align-middle"
                                    style={{ fontSize: "0.85rem" }}
                                  >
                                    {PAYMENT_METHOD_LABELS[
                                      row.pay_mode?.toLowerCase?.()
                                        ? row.pay_mode.toLowerCase()
                                        : row.pay_mode
                                    ] ||
                                      row.pay_mode ||
                                      ""}
                                  </td>
                                  <td
                                    className="p-2 text-center align-middle"
                                    style={{ fontSize: "0.85rem" }}
                                  >
                                    {(() => {
                                      return (
                                        row.pay_type ||
                                        row.bank_name ||
                                        PAYMENT_METHOD_LABELS[
                                          row.pay_mode?.toLowerCase?.()
                                            ? row.pay_mode.toLowerCase()
                                            : row.pay_mode
                                        ] ||
                                        row.pay_mode ||
                                        ""
                                      );
                                    })()}
                                  </td>
                                  <td
                                    className="p-2 align-middle"
                                    style={{ fontSize: "0.85rem" }}
                                  >
                                    {row.remarks}
                                  </td>
                                  <td
                                    className="p-2 text-end align-middle"
                                    style={{ fontSize: "0.85rem" }}
                                  >
                                    {parseFloat(
                                      row.amount || row.amt || 0
                                    ).toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-12 col-xl-3 border-start">
                  <div className="mb-4">
                    <h6
                      className="mb-3 fw-bold d-flex align-items-center"
                      style={{ color: GIRVI_THEME.bgColor }}
                    >
                      Item Details
                      <button
                        type="button"
                        className="btn d-flex align-items-center justify-content-center ms-2"
                        onClick={() => setShowProductDetailsModal(true)}
                        style={{
                          backgroundColor: GIRVI_THEME.bgColor,
                          color: GIRVI_THEME.textColor,
                          borderRadius: "8px",
                          minWidth: "32px",
                          width: "32px",
                          height: "24px",
                          padding: "0",
                          border: "none",
                        }}
                      >
                        <iconify-icon
                          icon="solar:eye-bold-duotone"
                          width="16"
                          height="16"
                          style={{ color: "#ffffff" }}
                        />
                      </button>
                    </h6>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <div className="small text-muted">Item Name</div>
                        <div
                          className="fw-semibold"
                          style={{
                            color: GIRVI_THEME.bgColor,
                            maxWidth: "260px",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            lineHeight: "1.25",
                          }}
                        >
                          {(() => {
                            const filledRow =
                              tableData.find((row) => {
                                const totalAmt = row.data.find(
                                  (c) => c.id === "totalAmt"
                                )?.value;
                                return totalAmt && parseFloat(totalAmt) > 0;
                              }) || tableData[0];
                            const pgCell = filledRow?.data?.find(
                              (c) => c.id === "productGroup"
                            );
                            const raw =
                              pgCell?.displayValue || pgCell?.value || "-";
                            const text = String(raw);
                            const parts = text.split(" - ");
                            return parts.length > 1
                              ? parts[parts.length - 1].trim()
                              : text;
                          })()}
                        </div>
                      </div>
                      <div>
                        <div
                          className="small text-muted"
                          style={{ fontWeight: 600 }}
                        >
                          Gr. Wt. -{" "}
                          <span
                            className="fw-semibold"
                            style={{ color: GIRVI_THEME.bgColor }}
                          >
                            {tableData
                              .reduce((sum, row) => {
                                const gwt = parseFloat(
                                  row.data.find((c) => c.id === "grWt")
                                    ?.value || 0
                                );
                                return sum + (isNaN(gwt) ? 0 : gwt);
                              }, 0)
                              .toFixed(2)}{" "}
                            Gm
                          </span>
                        </div>
                        <div
                          className="small text-muted"
                          style={{ fontWeight: 600 }}
                        >
                          Net. Wt. -{" "}
                          <span
                            className="fw-semibold"
                            style={{ color: GIRVI_THEME.bgColor }}
                          >
                            {tableData
                              .reduce((sum, row) => {
                                const nwt = parseFloat(
                                  row.data.find((c) => c.id === "netWt")
                                    ?.value || 0
                                );
                                return sum + (isNaN(nwt) ? 0 : nwt);
                              }, 0)
                              .toFixed(2)}{" "}
                            Gm
                          </span>
                        </div>
                        <div
                          className="small text-muted"
                          style={{ fontWeight: 600 }}
                        >
                          F. Wt. -{" "}
                          <span
                            className="fw-semibold"
                            style={{ color: GIRVI_THEME.bgColor }}
                          >
                            {tableData
                              .reduce((sum, row) => {
                                const fwt = parseFloat(
                                  row.data.find((c) => c.id === "fWt")
                                    ?.value || 0
                                );
                                return sum + (isNaN(fwt) ? 0 : fwt);
                              }, 0)
                              .toFixed(2)}{" "}
                            Gm
                          </span>
                        </div>
                        <div
                          className="small text-muted"
                          style={{ fontWeight: 600 }}
                        >
                          Total -{" "}
                          <span
                            className="fw-semibold"
                            style={{ color: GIRVI_THEME.bgColor }}
                          >
                            ₹ {calculateTotal().toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr
                    className="my-4"
                    style={{ borderTop: "1px solid #dcdcdc", opacity: 1 }}
                  />
                  <div>
                    <h6
                      className="mb-3 fw-bold d-flex align-items-center"
                      style={{ color: GIRVI_THEME.bgColor }}
                    >
                      Financial Summary
                    </h6>
                    <div className="mb-2">
                      <p
                        className="mb-1"
                        style={{
                          fontSize: "0.9rem",
                          color: "#495057",
                          fontWeight: "500",
                        }}
                      >
                        1. Principal Given: ₹{principalGiven || ""}
                      </p>
                    </div>
                    <div className="mb-2">
                      <p
                        className="mb-1"
                        style={{
                          fontSize: "0.9rem",
                          color: "#495057",
                          fontWeight: "500",
                        }}
                      >
                        2. Interest Taken: {interestPercent || "0"}%
                      </p>
                    </div>
                    <div className="mb-3">
                      <p
                        className="mb-1"
                        style={{
                          fontSize: "0.9rem",
                          color: "#495057",
                          fontWeight: "500",
                        }}
                      >
                        3. Monthly Interest: ₹
                        {calculateInterestAmount().toFixed(2)}
                      </p>
                    </div>
                    <div className="mb-3">
                      <label
                        className="form-label small fw-semibold mb-1"
                        style={{ color: GIRVI_THEME.bgColor }}
                      >
                        Remaining Amount
                      </label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        placeholder="0"
                        value={remaining}
                        readOnly
                        style={{
                          borderRadius: "8px",
                          borderColor: GIRVI_THEME.borderColor,
                        }}
                      />
                    </div>
                    <button
                      className={`btn d-flex align-items-center justify-content-center w-100 ${buttonClass}`}
                      onClick={handleSave}
                      style={{
                        backgroundColor: GIRVI_THEME.bgColor,
                        color: GIRVI_THEME.textColor,
                        borderColor: GIRVI_THEME.bgColor,
                        borderRadius: "8px",
                        padding: "8px 10px",
                        fontSize: "1rem",
                        fontWeight: "600",
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showProductDetailsModal && (
        <div
          className="modal fade show d-flex align-items-center justify-content-center"
          style={{
            display: "flex",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1050,
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
          onClick={() => setShowProductDetailsModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
            style={{ margin: 0, maxWidth: "75%", width: "75%" }}
          >
            <div
              className="modal-content"
              style={{ borderRadius: "12px", overflow: "hidden" }}
            >
              <div
                className="modal-header border-0 d-flex justify-content-between align-items-center"
                style={{
                  padding: "12px 16px",
                }}
              >
                <h5
                  className="modal-title d-flex align-items-center mb-0"
                  style={{ margin: "0" }}
                >
                  Product Details
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowProductDetailsModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body p-3">
                <div className="table-responsive">
                  <table
                    className={`table table-bordered table-hover table-sm mb-0 ${tableClass} theme-header-table`}
                  >
                    <thead>
                      <tr>
                        {COLUMNS.map((column) => (
                          <th
                            key={column.id}
                            className={`px-2 py-2 ${
                              column.id === "productGroup"
                                ? "text-start"
                                : "text-center"
                            }`}
                            style={{
                              fontSize: "0.8rem",
                              width: column.width,
                              fontWeight: "600",
                              whiteSpace: "nowrap",
                              borderColor: GIRVI_THEME.borderColor,
                            }}
                          >
                            {column.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((row, rowIndex) => (
                        <tr
                          key={row.id}
                          style={{
                            backgroundColor:
                              rowIndex % 2 === 0 ? "#ffffff" : "#fafafa",
                          }}
                        >
                          {row.data.map((cell, colIndex) => {
                            if (cell.id === "no") {
                              return (
                                <td
                                  key={cell.id}
                                  className="p-2 text-center"
                                  style={{ width: COLUMNS[colIndex]?.width }}
                                >
                                  <span
                                    style={{
                                      fontSize: "0.85rem",
                                      fontWeight: "600",
                                      color: GIRVI_THEME.bgColor,
                                    }}
                                  >
                                    {rowIndex + 1}
                                  </span>
                                </td>
                              );
                            }
                            if (cell.id === "productGroup") {
                              return (
                                <td
                                  key={cell.id}
                                  className="p-2 text-start"
                                  style={{ width: COLUMNS[colIndex]?.width }}
                                >
                                  {cell.displayValue || cell.value || ""}
                                </td>
                              );
                            }
                            return (
                              <td
                                key={cell.id}
                                className={`p-2 ${
                                  ["productGroup"].includes(cell.id)
                                    ? "text-start"
                                    : "text-end"
                                }`}
                                style={{
                                  width: COLUMNS[colIndex]?.width,
                                }}
                              >
                                {cell.value || cell.displayValue || ""}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                      <tr
                        style={{
                          backgroundColor: GIRVI_THEME.bgLight,
                          fontWeight: "600",
                        }}
                      >
                        {COLUMNS.map((col, colIndex) => {
                          if (col.id === "no") {
                            return (
                              <td
                                key={col.id}
                                className="p-2 text-center"
                                style={{ color: GIRVI_THEME.bgColor }}
                              >
                                Total
                              </td>
                            );
                          }
                          if (col.id === "pcs") {
                            return (
                              <td
                                key={col.id}
                                className="p-2 text-end"
                                style={{ color: "var(--theme-primary)" }}
                              >
                                {tableData.reduce((sum, row) => {
                                  const pcs = parseFloat(
                                    row.data.find((cell) => cell.id === "pcs")
                                      ?.value || 0
                                  );
                                  return sum + (isNaN(pcs) ? 0 : pcs);
                                }, 0)}
                              </td>
                            );
                          }
                          if (col.id === "grWt") {
                            return (
                              <td
                                key={col.id}
                                className="p-2 text-end"
                                style={{ color: "var(--theme-primary)" }}
                              >
                                {tableData
                                  .reduce((sum, row) => {
                                    const gwt = parseFloat(
                                      row.data.find(
                                        (cell) => cell.id === "grWt"
                                      )?.value || 0
                                    );
                                    return sum + (isNaN(gwt) ? 0 : gwt);
                                  }, 0)
                                  .toFixed(2)}
                              </td>
                            );
                          }
                          if (col.id === "lsWt") {
                            return (
                              <td
                                key={col.id}
                                className="p-2 text-end"
                                style={{ color: "var(--theme-primary)" }}
                              >
                                {tableData
                                  .reduce((sum, row) => {
                                    const lwt = parseFloat(
                                      row.data.find(
                                        (cell) => cell.id === "lsWt"
                                      )?.value || 0
                                    );
                                    return sum + (isNaN(lwt) ? 0 : lwt);
                                  }, 0)
                                  .toFixed(2)}
                              </td>
                            );
                          }
                          if (col.id === "netWt") {
                            return (
                              <td
                                key={col.id}
                                className="p-2 text-end"
                                style={{ color: "var(--theme-primary)" }}
                              >
                                {tableData
                                  .reduce((sum, row) => {
                                    const nwt = parseFloat(
                                      row.data.find(
                                        (cell) => cell.id === "netWt"
                                      )?.value || 0
                                    );
                                    return sum + (isNaN(nwt) ? 0 : nwt);
                                  }, 0)
                                  .toFixed(2)}
                              </td>
                            );
                          }
                          if (col.id === "fWt") {
                            return (
                              <td
                                key={col.id}
                                className="p-2 text-end"
                                style={{ color: "var(--theme-primary)" }}
                              >
                                {tableData
                                  .reduce((sum, row) => {
                                    const fwt = parseFloat(
                                      row.data.find(
                                        (cell) => cell.id === "fWt"
                                      )?.value || 0
                                    );
                                    return sum + (isNaN(fwt) ? 0 : fwt);
                                  }, 0)
                                  .toFixed(2)}
                              </td>
                            );
                          }
                          if (col.id === "itAmt") {
                            return (
                              <td
                                key={col.id}
                                className="p-2 text-end"
                                style={{ color: "var(--theme-primary)" }}
                              >
                                {tableData
                                  .reduce((sum, row) => {
                                    const itAmt = parseFloat(
                                      row.data.find(
                                        (cell) => cell.id === "itAmt"
                                      )?.value || 0
                                    );
                                    return sum + (isNaN(itAmt) ? 0 : itAmt);
                                  }, 0)
                                  .toFixed(2)}
                              </td>
                            );
                          }
                          if (col.id === "othAmt") {
                            return (
                              <td
                                key={col.id}
                                className="p-2 text-end"
                                style={{ color: "var(--theme-primary)" }}
                              >
                                {tableData
                                  .reduce((sum, row) => {
                                    const othAmt = parseFloat(
                                      row.data.find(
                                        (cell) => cell.id === "othAmt"
                                      )?.value || 0
                                    );
                                    return sum + (isNaN(othAmt) ? 0 : othAmt);
                                  }, 0)
                                  .toFixed(2)}
                              </td>
                            );
                          }
                          if (col.id === "mrp") {
                            return (
                              <td
                                key={col.id}
                                className="p-2 text-end"
                                style={{ color: "var(--theme-primary)" }}
                              >
                                {tableData
                                  .reduce((sum, row) => {
                                    const mrp = parseFloat(
                                      row.data.find((cell) => cell.id === "mrp")
                                        ?.value || 0
                                    );
                                    return sum + (isNaN(mrp) ? 0 : mrp);
                                  }, 0)
                                  .toFixed(2)}
                              </td>
                            );
                          }
                          if (col.id === "totalAmt") {
                            return (
                              <td
                                key={col.id}
                                className="p-2 text-end fw-bold"
                                style={{
                                  color: GIRVI_THEME.bgColor,
                                  fontSize: "0.9rem",
                                }}
                              >
                                {calculateTotal().toFixed(2)}
                              </td>
                            );
                          }
                          return <td key={col.id} className="p-0"></td>;
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Item Image Preview Modal */}
      {showItemImageModal && itemImagePreview && (
        <div
          className="modal fade show d-flex align-items-center justify-content-center"
          style={{
            display: "flex",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1050,
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
          onClick={() => setShowItemImageModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
            style={{ margin: 0 }}
          >
            <div
              className="modal-content"
              style={{
                borderRadius: "12px",
                overflow: "hidden",
                maxWidth: "600px",
                margin: "auto",
              }}
            >
              <div
                className="modal-header border-0 d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: "var(--theme-primary)",
                  padding: "16px 20px",
                }}
              >
                <h5
                  className="modal-title text-white d-flex align-items-center mb-0"
                  style={{ margin: "0" }}
                >
                  <iconify-icon
                    icon="solar:gallery-bold-duotone"
                    className="me-2"
                    style={{ fontSize: "20px" }}
                  />
                  Item Image
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowItemImageModal(false)}
                ></button>
              </div>
              <div
                className="modal-body text-center d-flex align-items-center justify-content-center"
                style={{ padding: "20px", minHeight: "300px" }}
              >
                <img
                  src={itemImagePreview}
                  alt="Item"
                  onError={(e) => {
                    const fallback =
                      getNodeImageCandidates(existingItemImageName)[1] || null;
                    if (fallback && e.currentTarget.src !== fallback) {
                      e.currentTarget.src = fallback;
                    }
                  }}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "500px",
                    borderRadius: "8px",
                    objectFit: "contain",
                    margin: "auto",
                  }}
                />
              </div>
              <div
                className="modal-footer border-0 d-flex justify-content-end"
                style={{ padding: "16px 20px" }}
              >
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setItemImage(null);
                    setItemImagePreview(null);
                    setExistingItemImageName(null);
                    if (itemImageInputRef.current) {
                      itemImageInputRef.current.value = "";
                    }
                    setShowItemImageModal(false);
                  }}
                  style={{
                    backgroundColor: "var(--theme-primary)",
                    color: GIRVI_THEME.textColor,
                    borderRadius: "8px",
                    padding: "8px 24px",
                  }}
                >
                  Remove Image
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Proof Image Modal */}
      {showUserProofModal && userProofImgPreview && (
        <div
          className="modal fade show d-flex align-items-center justify-content-center"
          style={{
            display: "flex",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1050,
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
          onClick={() => setShowUserProofModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
            style={{ margin: 0 }}
          >
            <div
              className="modal-content"
              style={{
                borderRadius: "12px",
                overflow: "hidden",
                maxWidth: "600px",
                margin: "auto",
              }}
            >
              <div
                className="modal-header border-0 d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: "var(--theme-primary)",
                  padding: "16px 20px",
                }}
              >
                <h5
                  className="modal-title text-white d-flex align-items-center mb-0"
                  style={{ margin: "0" }}
                >
                  <iconify-icon
                    icon="solar:gallery-bold-duotone"
                    className="me-2"
                    style={{ fontSize: "20px" }}
                  />
                  User Proof Image
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowUserProofModal(false)}
                ></button>
              </div>
              <div
                className="modal-body text-center d-flex align-items-center justify-content-center"
                style={{ padding: "20px", minHeight: "300px" }}
              >
                <img
                  src={userProofImgPreview}
                  alt="User Proof Image"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "500px",
                    borderRadius: "8px",
                    objectFit: "contain",
                    margin: "auto",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Signature Modal */}
      {showSignatureModal && customerSignaturePreview && (
        <div
          className="modal fade show d-flex align-items-center justify-content-center"
          style={{
            display: "flex",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1050,
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
          onClick={() => setShowSignatureModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
            style={{ margin: 0 }}
          >
            <div
              className="modal-content"
              style={{
                borderRadius: "12px",
                overflow: "hidden",
                maxWidth: "600px",
                margin: "auto",
              }}
            >
              <div
                className="modal-header border-0 d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: "var(--theme-primary)",
                  padding: "16px 20px",
                }}
              >
                <h5
                  className="modal-title text-white d-flex align-items-center mb-0"
                  style={{ margin: "0" }}
                >
                  <iconify-icon
                    icon="solar:pen-bold-duotone"
                    className="me-2"
                    style={{ fontSize: "20px" }}
                  />
                  Customer Signature
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowSignatureModal(false)}
                ></button>
              </div>
              <div
                className="modal-body text-center d-flex align-items-center justify-content-center"
                style={{ padding: "20px", minHeight: "300px" }}
              >
                <img
                  src={customerSignaturePreview}
                  alt="Customer Signature"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "500px",
                    borderRadius: "8px",
                    objectFit: "contain",
                    margin: "auto",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default EditGirvi;
